import { motion } from "framer-motion";

const toBalls = (o) => { const [a, b] = String(o || "0").split("."); return Number(a) * 6 + Number(b || 0); };
const lbl = (b) => (b.is_wicket ? "W" : b.is_wide ? "wd" : b.is_no_ball ? "nb" : String(b.runs ?? 0));
const legal = (b) => !b.is_wide && !b.is_no_ball;
const dot = (v) => (v === "W" ? "bg-cr-danger text-white" : v === "6" ? "bg-violet-500 text-white" : v === "4" ? "bg-sky-500 text-white" : v === "0" ? "bg-white/15 text-cr-ink" : v === "wd" || v === "nb" ? "bg-amber-500 text-black" : "bg-emerald-600 text-white");
const EV = { W: "WICKET", 6: "SIX", 4: "FOUR", 0: "Dot Ball", wd: "Wide", nb: "No Ball" };
const evText = (v) => EV[v] || `${v} Run${v === "1" ? "" : "s"}`;
const sum = (bs) => bs.reduce((s, b) => s + (b.runs || 0), 0);

const Chip = ({ v, i }) => <span data-testid={`lc-ball-${i}`} className={`grid h-6 w-6 shrink-0 sm:h-8 sm:w-8 place-items-center rounded-full text-xs font-black ${dot(v)}`}>{v}</span>;
const Empty = () => <span className="h-6 w-6 shrink-0 sm:h-8 sm:w-8 rounded-full border border-white/30" />;
const Card = ({ children, testid, className = "" }) => <div data-testid={testid} className={`rounded-lg border border-cr-line bg-[#121414] px-4 py-3 ${className}`}>{children}</div>;
const Pair = ({ a, b, testid }) => (
  <span data-testid={testid} className="flex gap-1.5"><b className="min-w-12 rounded bg-[#faa9ba]/20 px-2 py-1 text-center text-[#faa9ba]">{a ?? "-"}</b><b className="min-w-12 rounded bg-[#72bbef]/20 px-2 py-1 text-center text-[#72bbef]">{b ?? "-"}</b></span>
);

const useOvers = (c, team) => {
  const inn = c.filter((b) => b.team_name === team);
  if (!inn.length) return null;
  const ov = inn[0].over;
  const cur = inn.filter((b) => b.over === ov).reverse();
  if (cur.filter(legal).length >= 6) return { prev: cur, prevNo: ov + 1, cur: [], curNo: ov + 2 };
  return { prev: inn.filter((b) => b.over === ov - 1).reverse(), prevNo: ov, cur, curNo: ov + 1 };
};

const OversRow = ({ o, last6 }) => (
  <Card testid="lc-overs" className="flex flex-wrap items-center gap-2">
    {o ? <>
      {o.prev.length > 0 && <div className="flex items-center gap-1">{o.prev.map((b, i) => <Chip key={b.id ?? i} v={lbl(b)} i={`p${i}`} />)}<span data-testid="lc-prev-total" className="ml-1 shrink-0 whitespace-nowrap font-bold">= {sum(o.prev)}</span><span className="mx-2 hidden h-8 w-px bg-cr-line sm:block" /></div>}
      <div className="flex items-center gap-1"><span className="mr-1 shrink-0 text-sm font-semibold">Over {o.curNo}</span>
      <div className="flex gap-1">{o.cur.map((b, i) => <Chip key={b.id ?? i} v={lbl(b)} i={`c${i}`} />)}{Array.from({ length: Math.max(0, 6 - o.cur.filter(legal).length) }, (_, i) => <Empty key={i} />)}</div>
      <span data-testid="lc-cur-total" className="ml-1 shrink-0 whitespace-nowrap font-bold">= {sum(o.cur)}</span></div>
    </> : <><span className="text-sm text-cr-muted">Last balls</span><div className="flex gap-1.5">{last6.map((v, i) => <Chip key={i} v={v} i={i} />)}</div></>}
  </Card>
);

const crease = (live, team) => {
  const c = (live?.commentary || []).filter((b) => b.team_name === team);
  const bat = (live?.batting || []).filter((b) => b.team_name === team);
  const lastW = c.findIndex((b) => b.is_wicket);
  const since = lastW === -1 ? c : c.slice(0, lastW);
  const names = [...new Set(since.map((b) => b.batsman_name))].slice(0, 2);
  let rows = names.map((n) => bat.find((b) => b.player_name === n)).filter(Boolean);
  if (rows.length < 2) rows = [...rows, ...bat.filter((b) => !b.is_out && !rows.includes(b))].slice(-2);
  const out = lastW === -1 ? null : bat.find((b) => b.player_name === c[lastW].batsman_name);
  const ps = lastW === -1 ? { r: rows.reduce((s, b) => s + (b.score || 0), 0), b: rows.reduce((s, b) => s + (b.balls || 0), 0) } : { r: sum(since), b: since.filter(legal).length };
  const bw = (live?.bowling || []).find((w) => w.player_name === c[0]?.bowler_name);
  return { rows, striker: c[0]?.batsman_name, out, ps, bw };
};

const Th = ({ cols }) => <tr className="text-xs text-cr-muted">{cols.map((h, i) => <th key={h} className={`py-1.5 font-medium ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr>;

const Players = ({ k }) => (
  <Card testid="lc-players">
    <table className="w-full text-sm"><thead><Th cols={["Batter", "R", "B", "4s", "6s", "SR"]} /></thead>
      <tbody>{k.rows.map((b) => (
        <tr key={b.player_name} data-testid="lc-batter" className="border-t border-cr-line">
          <td className="py-2 font-semibold">{b.player_name}{b.player_name === k.striker && <span className="ml-1 text-cr-gold">*</span>}</td>
          <td className="text-right font-bold">{b.score}</td><td className="text-right">{b.balls}</td><td className="text-right">{b.fours}</td><td className="text-right">{b.sixes}</td>
          <td className="text-right">{b.balls ? (b.score * 100 / b.balls).toFixed(2) : "0.00"}</td></tr>))}</tbody></table>
    <div className="mt-2 flex justify-between border-t border-cr-line pt-2 text-xs text-cr-muted">
      <span data-testid="lc-partnership">P'ship: <b className="text-cr-ink">{k.ps.r}({k.ps.b})</b></span>
      {k.out && <span data-testid="lc-last-wicket">L' Wkt: <b className="text-cr-ink">{k.out.player_name} {k.out.score}({k.out.balls})</b></span>}
    </div>
    {k.bw && <table className="mt-3 w-full text-sm"><thead><Th cols={["Bowler", "O", "R", "Wkt", "Eco"]} /></thead>
      <tbody><tr data-testid="lc-bowler" className="border-t border-cr-line"><td className="py-2 font-semibold">{k.bw.player_name} <span className="text-cr-danger">●</span></td>
        <td className="text-right">{k.bw.overs}</td><td className="text-right">{k.bw.runs}</td><td className="text-right font-bold">{k.bw.wickets}</td>
        <td className="text-right">{toBalls(k.bw.overs) ? (k.bw.runs * 6 / toBalls(k.bw.overs)).toFixed(2) : "-"}</td></tr></tbody></table>}
  </Card>
);

const Projected = ({ cur, format }) => {
  const done = toBalls(cur.overs);
  if (!done) return null;
  const crr = cur.runs * 6 / done, left = (format * 6 - done) / 6;
  const rates = [crr, Math.ceil(crr + 0.01), Math.ceil(crr + 0.01) + 1, Math.ceil(crr + 0.01) + 2];
  return (
    <Card testid="lc-projected" className="overflow-x-auto">
      <table className="w-full text-sm"><tbody>
        <tr><td className="py-1 font-semibold">Run Rate</td>{rates.map((r, i) => <td key={i} className="text-right text-cr-muted">{i ? r : `${r.toFixed(2)}*`}</td>)}</tr>
        <tr><td className="py-1 font-semibold">Projected Score</td>{rates.map((r, i) => <td key={i} data-testid={`lc-proj-${i}`} className="text-right font-bold">{Math.round(cur.runs + r * left)}</td>)}</tr>
      </tbody></table>
    </Card>
  );
};

export default function LiveCenter({ x, live: raw, cur, short }) {
  const s = x.score, fmt = x.format;
  const c0 = raw?.commentary?.[0];
  const fresh = !c0 || Math.abs(c0.over * 6 + c0.ball_in_over - toBalls(cur.overs)) <= 6;
  const live = fresh ? raw : null;
  const c = live?.commentary || [];
  const o = useOvers(c, cur.team);
  const lastV = c[0]?.team_name === cur.team ? lbl(c[0]) : s.last6[s.last6.length - 1];
  const big = x.hold?.reason || (lastV != null && lastV !== "-" && lastV !== "" ? evText(lastV) : s.ball_state && s.ball_state !== "-" ? s.ball_state : "Live");
  const ballsLeft = fmt ? fmt * 6 - toBalls(cur.overs) : null;
  const fav = [...(x.odds || [])].filter((r) => r.lagai != null).sort((a, b) => a.lagai - b.lagai)[0];
  const ses = (x.sessions || []).find((q) => q.key?.startsWith("ov:")) || x.sessions?.[0];
  const second = s.innings.length === 2 && fmt;
  const need = second ? { r: s.innings[0].runs + 1 - cur.runs, b: ballsLeft } : null;
  const k = crease(live, cur.team);
  return (
    <div data-testid="live-center" className="mt-3 space-y-2">
      <div className="overflow-hidden rounded-lg border-4 border-black bg-black">
        <div className="relative grid h-32 place-items-center">
          <span className="absolute right-3 top-2 flex items-center gap-1 text-xs"><span className="h-2 w-2 rounded-full bg-red-500" />Live</span>
          <motion.span key={`${big}-${cur.runs}-${cur.wkts}-${cur.overs}`} initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} data-testid="lc-event"
            className={`font-display text-4xl font-black ${lastV === "W" ? "text-cr-danger" : lastV === "6" || lastV === "4" ? "text-cr-gold" : "text-white"}`}>{big}</motion.span>
        </div>
        <div data-testid="lc-score" className="bg-white px-4 py-2 text-black"><b className="mr-2">{short}</b><b className="text-2xl">{cur.runs}-{cur.wkts}</b> <span className="text-lg">({cur.overs})</span></div>
      </div>
      {(o || s.last6?.length > 0) && <OversRow o={o} last6={s.last6} />}
      <Card testid="lc-rates" className="flex justify-between text-base font-semibold"><span>Run Rate: <b data-testid="lc-crr">{s.crr}</b>{s.rrr != null && <> · RRR: <b>{s.rrr}</b></>}</span>{ballsLeft != null && ballsLeft > 0 && <span data-testid="lc-balls-rem">Balls Rem: {ballsLeft}</span>}</Card>
      {(fav || ses || need) && <Card testid="lc-markets" className="space-y-3">
        {fav && <div className="flex items-center justify-between"><span className="font-semibold">Win Rate</span><span className="flex items-center gap-2 text-sm">{fav.name}: <Pair a={fav.khai} b={fav.lagai} testid="lc-win-pair" /></span></div>}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-cr-line pt-3">
          {ses && <span className="flex items-center gap-2 text-sm font-semibold">{ses.name}: <Pair a={ses.no} b={ses.yes} testid="lc-session-pair" /></span>}
          {need && need.r > 0 && <span className="flex items-center gap-2 text-sm font-semibold">R X B: <Pair a={need.r} b={need.b} testid="lc-rxb" /></span>}
        </div>
      </Card>}
      {k.rows.length > 0 && <Players k={k} />}
      {fmt && !second && <Projected cur={cur} format={fmt} />}
    </div>
  );
}
